<!-- eslint-disable max-len -->
<template>
  <div class="my-stays">
    <simple-list
      ref="list"
      :server-items="items"
      query="status"
      :undefined-value="tc('reservations.allMyStaysLabel')"
      class=""
      variant="my-stays-list"
    >
      <template #filter>
        <div
          v-if="stays.length > 0"
          class="filter"
          data-component="booking-room-filters"
        >
          <accessible-tabs>
            <div
              ref="myStays"
              role="tablist"
              class="filter-items"
              :class="[{ 'no-upcoming': !isUpcomingExist }]"
            >
              <button
                id="stay-filter-all"
                href=""
                class="filter-item tab-group-button"
                :class="[{ active: !status }, 'all']"
                role="tab"
                :aria-selected="!status"
                :tabindex="!status ? '0' : '-1'"
                @click.prevent="filterByStatus()"
                @keydown.right.prevent="filterByStatus(statusItems[0])"
                @keydown.left.prevent="filterByStatus(statusItems[statusItems.length - 1])"
              >
                {{ tc('reservations.allMyStaysLabel') }}
              </button>

              <button
                v-for="(item, i) in statusItems"
                :id="'stay-filter-' + i"
                :key="item.code"
                href=""
                class="filter-item tab-group-button"
                :class="[{ active: status === item }, item.cardType]"
                role="tab"
                :aria-controls="status ? status.code : ''"
                :aria-selected="item === status"
                :tabindex="item === status ? '0' : '-1'"
                @click.prevent="filterByStatus(item)"
                @keydown.right.prevent="filterByStatus(i === statusItems.length - 1 ? null : statusItems[0])"
                @keydown.left.prevent="filterByStatus(i === 0 ? null : statusItems[statusItems.length - 1])"
              >
                {{ item.label }}
              </button>
            </div>
          </accessible-tabs>
        </div>
      </template>
      <template #rendered-list="{ items: filteredItems }">
        <ul>
          <flipping-card
            v-for="item in filteredItems"
            :key="item.id"
            :card="item"
            :status-data="getStatusDataByCode(item.status)"
            :modify-page="modifyPage"
          />
        </ul>
      </template>
      <template #pagination="{ pagination, defaultLimit, allLoaded }">
        <pagination
          :total-pages="totalPages"
          :current-page="currentPage"
          @prev-page="prevPage"
          @next-page="nextPage"
          @goto-page="gotoPage"
        />
      </template>
    </simple-list>
  </div>
</template>

<script>
  import moment from 'moment'
  import axios from 'axios'
  import { getApiUrl } from '@utils/api-helpers'
  import { mapActions, mapGetters } from 'vuex'
  import Pagination from '../../01.base/02.lists/02.pagination-types/pagination.vue'

  export default {
    name: 'MyStays',
    components: {
      Pagination,
    },
    props: {
      apiUrl: {
        type: String,
        default: 'api/v1/booking/reservations?filterPastReservations=false'
      },
      lang: {
        type: Object,
        required: true
      },
      modifyPage: {
        type: String,
        required: false
      },
    },
    data: () => ({
      isLoading: false,
      stays: [],
      filteredStays: [],
      status: '',
      items: [],
      currentPage: 1,
      itemsPerPage: 9,
      statusesData: {
        history: {
          code: 'H',
          label: '',
          cardType: 'past'
        },
        upcoming: {
          code: 'U',
          label: '',
          cardType: 'upcoming'
        },
        cancelled: {
          code: 'X',
          label: '',
          cardType: 'cancelled'
        },
        XNS: ''
      }
    }),
    computed: {
      ...mapGetters('i18n', ['tc']),
      statusItems() {
        const statuses = []
        const existingStatuses = this.stays.map((r) => r.status)
        existingStatuses.forEach((code) => {
          const statusObj = Object.values(this.statusesData).find((s) => s.code === code)
          if (statusObj && !statuses.includes(statusObj)) {
            statuses.push(statusObj)
          }
        })
        return statuses
      },
      isUpcomingExist() {
        return this.stays.some((r) => r.status === this.statusesData.upcoming.code)
      },
      totalPages() {
        return Math.ceil(this.filteredStays.length / this.itemsPerPage)
      }
    },
    watch: {
      async isLoading(val) {
        if (!val) {
          await this.checkReservations()
        }
      },
      currentPage(newPage) {
        this.loadItems(newPage)
      },
      status() {
        this.loadItems(1)
      }
    },
    async created() {
      await this.registerLang({ scope: 'reservations', lang: this.lang })
    },
    async mounted() {
      await this.checkReservations()
      this.statusLabels()
      this.filterProperties({})
      this.loadItems(this.currentPage)
      if (this.isUpcomingExist) this.filterByStatus(this.statusesData.upcoming)
    },
    methods: {
      ...mapActions('i18n', ['registerLang']),
      statusLabels() {
        if (this.tc('reservations.pastLabel')) this.statusesData.history.label = this.tc('reservations.pastLabel')
        if (this.tc('reservations.upcomingBackLabel')) this.statusesData.upcoming.label = this.tc('reservations.upcomingBackLabel')
        if (this.tc('reservations.cancelledLabel')) this.statusesData.cancelled.label = this.tc('reservations.cancelledLabel')
      },
      getStatusDataByCode(code) {
        return Object.values(this.statusesData).find((status) => status.code === code) || ''
      },
      filterProperties(filter) {
        if (filter && filter.status) {
          const statusCode = filter.status.code
          this.$refs.list.updateFilter({ status: statusCode })
        } else {
          this.$refs.list.updateFilter({})
        }
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage -= 1
          this.scrollToTop()
        }
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage += 1
          this.scrollToTop()
        }
      },
      gotoPage(page) {
        if (page >= 1 && page <= this.totalPages) {
          this.currentPage = page
          this.scrollToTop()
        }
      },
      filterByStatus(status) {
        this.status = status
        this.currentPage = 1
        if (status) {
          this.filterProperties({ status })
          this.filteredStays = this.stays.filter((item) => {
            const statusObj = Object.values(this.statusesData).find((s) => s.code === item.status)
            return statusObj && statusObj.label === status.label
          })
        } else {
          this.filterProperties({})
          this.filteredStays = this.stays.filter((item) => item.status !== 'XNS')
        }

        this.filteredStays.sort((a, b) => {
          const checkInDateA = moment(a.startDate)
          const checkInDateB = moment(b.startDate)
          const checkOutDateA = moment(a.endDate)
          const checkOutDateB = moment(b.endDate)

          if (!status || status.code === 'X') {
            if (checkInDateA.isBefore(checkInDateB)) return 1
            if (checkInDateA.isAfter(checkInDateB)) return -1
          } else if (status.code === 'H') {
            if (checkOutDateA.isBefore(checkOutDateB)) return 1
            if (checkOutDateA.isAfter(checkOutDateB)) return -1
          } else {
            if (checkInDateA.isBefore(checkInDateB)) return -1
            if (checkInDateA.isAfter(checkInDateB)) return 1
          }
          return 0
        })
      },
      async checkReservations() {
        const myStaysSection = document.querySelector('.flipping-card-component')
        const url = getApiUrl(this.apiUrl)
        const { data } = await axios.get(url)
        if (data) {
          this.stays = data
          this.filteredStays = data
          if (this.stays.length === 0) {
            myStaysSection.style.display = 'none'
          }
        } else {
          myStaysSection.style.display = 'none'
        }
      },
      loadItems(page) {
        const startIndex = (page - 1) * this.itemsPerPage
        this.items = this.filteredStays.slice(startIndex, startIndex + this.itemsPerPage)
      },
      isElementInView(el) {
        const rect = el.getBoundingClientRect()
        return (
          rect.top >= 0
          && rect.left >= 0
          && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
          && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        )
      },
      scrollToTop() {
        const el = document.querySelector('.flipping-card__heading')
        if (el && !this.isElementInView(el)) {
          const topPos = el.getBoundingClientRect().top + window.scrollY
          window.scrollTo({
            top: topPos,
            behavior: 'smooth'
          })
        }
      }
    }
  }
</script>
